import { api, tokenStorage, refreshTokenWithMutex } from '../services/api';

// Re-export tokenStorage for other files that import from auth.ts
export { tokenStorage };

// Auth API functions
export const authApi = {
  async login(email: string, password: string) {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get('/user/me');
    return response.data;
  },

  async register(userData: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    preferred_language?: string;
    accept_terms?: boolean;
    hcaptcha_token?: string;
    honeypot_field?: string;
  }) {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  async googleAuth(credential: string, preferredLanguage?: string | null, acceptTerms: boolean = true) {
    const fromStorage = typeof localStorage !== 'undefined' ? localStorage.getItem('preferred_language') : null;
    const fromNav = typeof navigator !== 'undefined' ? navigator.language?.substring(0, 2) : null;
    const lang = preferredLanguage ?? fromStorage ?? fromNav ?? 'en';
    const validLanguages = ['en', 'fr', 'es'];
    const preferred_language = validLanguages.includes(lang) ? lang : 'en';
    const body = { credential, preferred_language, accept_terms: acceptTerms };
    const response = await api.post('/auth/google', body);
    return response.data;
  },
};

// Main auth functions
export const login = async (email: string, password: string) => {
  // console.log('🔐 LOGIN START:', { email, password: '***' });

  try {
    // console.log('📡 Calling authApi.login...');
    const response = await authApi.login(email, password);
    // console.log('✅ Login API response:', response);

    if (response?.access_token && response?.refresh_token) {
      // console.log('💾 Storing tokens...');
      tokenStorage.setTokens(response.access_token, response.refresh_token);

      // console.log('👤 Getting user data...');
      // Get user data after successful login
      const user = await authApi.getCurrentUser();
      // console.log('✅ User data:', user);

      return { success: true, user };
    }

    // console.log('❌ Invalid response from login:', response);
    throw new Error('Invalid response from login');
  } catch (error) {
    // console.log('❌ Login error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Login failed'
    };
  }
};

export const googleLogin = async (credential: string, preferredLanguage?: string | null, acceptTerms: boolean = true) => {
  try {
    const response = await authApi.googleAuth(credential, preferredLanguage, acceptTerms);

    if (response?.access_token && response?.refresh_token) {
      tokenStorage.setTokens(response.access_token, response.refresh_token);

      const user = await authApi.getCurrentUser();

      return { success: true, user };
    }

    throw new Error('Invalid response from Google authentication');
  } catch (error: any) {
    // Extract error message from axios response
    let errorMessage = 'Google authentication failed';
    if (error?.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    return {
      success: false,
      error: errorMessage
    };
  }
};

export const register = async (userData: {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  preferred_language?: string;
  accept_terms?: boolean;
  hcaptcha_token?: string;
  honeypot_field?: string;
}) => {
  try {
    const response = await authApi.register(userData);
    return { success: true, user: response };
  } catch (error: any) {
    // Extract error message from axios response
    let errorMessage = 'Registration failed';
    if (error?.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    return {
      success: false,
      error: errorMessage
    };
  }
};

export const logout = () => {
  tokenStorage.clearTokens();
};

export const getUser = async () => {
  try {
    return await authApi.getCurrentUser();
  } catch (error) {
    // If getting user fails, clear tokens
    tokenStorage.clearTokens();
    throw error;
  }
};

// Re-export the mutex-protected refresh function for external use
// This ensures all token refresh calls go through the same mutex
export const refreshToken = refreshTokenWithMutex;
